import Phaser from 'phaser';
import type { VehicleDef } from '@/data/vehicles';

/**
 * Vehicle — mountable world prop (forklift, downed biker's bike).
 *
 * Thin Phaser visual + tag. All HP and mount-state logic lives in
 * VehicleState (pure) owned by the Player who mounts this entity.
 *
 * TODO(MY-1800): bike instances spawned by Enemy adapter on wantsKnockOff.
 * TODO(MY-1803): forklift placed by StageScene prop layer for Stage 3.
 */
export class Vehicle extends Phaser.GameObjects.Container {
  readonly def: VehicleDef;

  /** True while a player is mounted — prevents double-mount. */
  isMounted = false;

  private readonly bodyRect: Phaser.GameObjects.Rectangle;

  constructor(scene: Phaser.Scene, x: number, y: number, def: VehicleDef) {
    super(scene, x, y);
    this.def = def;

    const VISUALS: Record<string, { w: number; h: number; color: number; label: string }> = {
      forklift: { w: 120, h: 80, color: 0xffcc00, label: 'FORK' },
      bike: { w: 90, h: 60, color: 0x444444, label: 'BIKE' },
      'tube-trolley': { w: 100, h: 70, color: 0xcc6600, label: 'TROLLEY' },
      'market-scooter': { w: 80, h: 55, color: 0xcc2200, label: 'SCOOT' },
    };
    const vis = VISUALS[def.kind] ?? {
      w: 90,
      h: 60,
      color: 0x888888,
      label: def.kind.toUpperCase(),
    };
    const { w, h, color, label } = vis;

    // vehicle_<kind> sprite if registered (else colour-rect + label). bodyRect is
    // kept (hidden when art) so onDestroyed's fade still has a target; the
    // container-level setAlpha(0.5) fades the sprite too.
    const texKey = `vehicle_${def.kind}`;
    const hasArt = scene.textures.exists(texKey);
    this.bodyRect = scene.add.rectangle(0, 0, w, h, color).setStrokeStyle(2, 0xffffff);
    this.bodyRect.setVisible(!hasArt);
    const children: Phaser.GameObjects.GameObject[] = [this.bodyRect];
    if (hasArt) {
      children.push(scene.add.image(0, 0, texKey).setDisplaySize(w, h));
    } else {
      children.push(
        scene.add
          .text(0, 0, label, { fontSize: '11px', color: '#ffffff', align: 'center' })
          .setOrigin(0.5),
      );
    }
    this.add(children);
    scene.add.existing(this);
  }

  /** Called by Player when the vehicle's HP reaches 0 (forced dismount). */
  onDestroyed(): void {
    this.isMounted = false;
    this.bodyRect.setAlpha(0.3);
    this.setAlpha(0.5);
  }
}
